// Egy személy GYEREKEI — ismételhető sorok (teljes név · születési dátum), az `AccountDomainsField` vizuális és
// működési mintájára (keretes sor, szellem-gombos törlés, körvonalas „+ Gyerek hozzáadása"). KÉT helyen él: a
// munkatárs-adatlapon (HR) és a belső Profilomon (a dolgozó maga) — ezért a közös rétegben, a mező-név
// paraméterként (`name`). A tartalmi szabály (mindkét mező kötelező, max. 20) a szerződésé (`PersonChildSchema`).
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { MAX_CHILDREN } from '@atlas/contracts';
import {
  type ArrayPath,
  type FieldArray,
  type FieldValues,
  type Path,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateField, TextField } from '@/components/shared/form-fields';
import { Button } from '@/components/ui/button';

export interface PersonChildrenFieldProps<T extends FieldValues> {
  /** A gyerek-lista mezője a formban (`{ fullName, birthDate }[]`). */
  name: ArrayPath<T>;
  disabled?: boolean;
}

export function PersonChildrenField<T extends FieldValues>({
  name,
  disabled = false,
}: PersonChildrenFieldProps<T>): JSX.Element {
  const { t } = useTranslation();
  const { control } = useFormContext<T>();
  // a `keyName` külön kulcs, hogy a sor értékei között ne jelenjen meg egy belső `id`
  const array = useFieldArray<T, ArrayPath<T>, 'key'>({ control, name, keyName: 'key' });
  const full = array.fields.length >= MAX_CHILDREN;

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">{t('people.children.title')}</p>

      {array.fields.length === 0 && (
        <p className="text-muted-foreground text-sm">{t('people.children.empty')}</p>
      )}

      {array.fields.map((field, index) => (
        <div
          key={field.key}
          // `sm:items-start`: a név alatti hibaüzenet NE tolja el a dátum-oszlopot (elrendezés-ugrás → egy
          // épp induló kattintás a dátum-gombon „elveszne")
          className="border-border grid grid-cols-1 gap-3 rounded-lg border p-3 sm:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] sm:items-start"
        >
          <TextField<T>
            name={`${name}.${index}.fullName` as Path<T>}
            label={t('people.children.fullName')}
            required
            disabled={disabled}
            autoComplete="off"
          />
          {/* A törlés a dátum mellett áll (a sor jobb alsó sarka) — mobilon sem lóg ki külön sorba. */}
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <DateField<T>
                name={`${name}.${index}.birthDate` as Path<T>}
                label={t('people.children.birthDate')}
                required
                disabled={disabled}
              />
            </div>
            {!disabled && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('people.children.remove')}
                onClick={() => array.remove(index)}
              >
                <TrashIcon className="text-destructive h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ))}

      {!disabled && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={full}
          // NINCS automatikus fókusz az új sorra: a fókusz elhagyása (pl. egy dátum-kattintásnál) a még üres név-mezőt
          // validálná, és a megjelenő hiba eltolná a sort a kattintás alatt
          onClick={() =>
            array.append(
              { fullName: '', birthDate: '' } as unknown as FieldArray<T, ArrayPath<T>>,
              {
                shouldFocus: false,
              },
            )
          }
        >
          <PlusIcon className="h-4 w-4" />
          {t('people.children.add')}
        </Button>
      )}
    </div>
  );
}
